/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { useEffect, useContext, useState, useCallback } from 'react';

import { QuickSetupContext } from '../components/common/quick-setup/QuickSetupProvider';

export const useQuickSetupContext = () => useContext(QuickSetupContext);

export const useQuickSetup = ({
  readOnly = false,
  backClicked = 0,
  nextClicked = 0,
  setEnableNext = (_show: boolean) => {},
  setEnableBack = (_show: boolean) => {},
  setShowNext = (_show: boolean) => {},
  setShowBack = (_show: boolean) => {},
  setFinished = (_show: boolean) => {}
}) => {
  // If backClick time changes, change the step.
  // HandleBackClick basically...
  const [currentStep, setCurrentStep] = useState(0);
  const [nextClickTime, setNextClickTime] = useState(null);
  const [backClickTime, setBackClickTime] = useState(null);
  const [steps, setSteps] = useState([]);
  const [filteredSteps, setFilteredSteps] = useState([]);
  const [stepFilter, setStepFilter] = useState(null);

  // Change the current step based on integer value
  const changeStep = useCallback(
    value => {
      const newCurrentStep = currentStep + value;
      setCurrentStep(newCurrentStep > 0 ? newCurrentStep : 0);
    },
    [currentStep]
  );

  // Disable step
  const shouldDisableStep = useCallback(
    stepNumber => {
      // Disable All if readonly
      return readOnly
        ? readOnly
        : filteredSteps
        ? filteredSteps
            .filter((_step, index) => index < stepNumber)
            .reduce((current, next) => {
              return current || (next.required && !next.readyToContinue);
            }, false)
        : null;
    },
    [filteredSteps, readOnly]
  );

  const markStepComplete = useCallback(
    value => {
      const newSteps = [...filteredSteps];
      const complete = typeof value === 'boolean' ? value : true;
      // Only want to do any of this updating IF our value is different than in past.
      if (newSteps[currentStep].readyToContinue !== complete) {
        newSteps[currentStep].readyToContinue = complete;
        setEnableNext(complete);
        setFilteredSteps(newSteps);
      }
    },
    [currentStep, filteredSteps, setEnableNext]
  );

  const clearSteps = () => {
    const newSteps = [...filteredSteps];
    newSteps.forEach((_step, index) => {
      if (index > 0) {
        newSteps[index].readyToContinue = false;
      }
    });
  };

  // Handle backClicked (changes step)
  useEffect(() => {
    // backClicked doesn't count if null
    if (backClicked && backClicked !== backClickTime) {
      setBackClickTime(backClicked);
      changeStep(-1);
    }
  }, [backClickTime, backClicked, changeStep]);

  // If nextClick time changes, change the step.
  // HandleNextClick basically...
  useEffect(() => {
    //nextClicked is no real if null
    if (nextClicked && nextClicked !== nextClickTime) {
      setNextClickTime(nextClicked);
      changeStep(1);
    }
  }, [changeStep, nextClickTime, nextClicked]);

  // Disable Next button if next step is disabled
  // If current step changed because a progress indicator step was clicked,
  // make next disabled re-render to apply to current step
  useEffect(() => {
    if (shouldDisableStep(currentStep + 1)) {
      setEnableNext(false);
    } else {
      setEnableNext(true);
    }
  }, [currentStep, setEnableNext, shouldDisableStep]);

  // Enable Back button if not first step
  useEffect(() => {
    setEnableBack(currentStep !== 0);
  }, [currentStep, setEnableBack]);

  // Enable showing Back button if not first step.
  useEffect(() => {
    setShowBack(currentStep !== 0);
  }, [currentStep, setShowBack]);

  // If Steps change, redo filter.
  useEffect(() => {
    if (steps) {
      const filterSteps = step => {
        if (Array.isArray(step.stepType)) {
          return step.stepType.includes(stepFilter);
        }
        return (
          !Boolean(step.stepType) ||
          step.stepType === 'all' ||
          step.stepType === stepFilter
        );
      };
      setFilteredSteps(steps.filter(filterSteps));
    }
  }, [stepFilter, steps]);

  // If we are at the last step, enable finished, disable
  // otherwise.
  useEffect(() => {
    setFinished(currentStep === filteredSteps?.length - 1);
  }, [currentStep, setFinished, filteredSteps]);

  // If we are at the last step, disable showing Next button.
  useEffect(() => {
    setShowNext(currentStep !== filteredSteps?.length - 1);
  }, [currentStep, setShowNext, filteredSteps]);

  return {
    clearSteps,
    setEnableNext,
    setFinished,
    setEnableBack,
    stepFilter,
    setStepFilter,
    currentStep,
    setCurrentStep,
    steps,
    setSteps,
    readOnly,
    filteredSteps,
    shouldDisableStep,
    setFilteredSteps,
    markStepComplete,
    setShowNext,
    setShowBack
  };
};
